import { jsx as _jsx } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle, BarChart3, BookA, Building2, Calendar, CalendarClock, ClipboardCheck, Contact, DollarSign, FilePlus2, FileSpreadsheet, FileText, Handshake, Layers, LayoutDashboard, ListChecks, ListTodo, MapPin, MessagesSquare, Scan, Settings2, Shield, ShieldCheck, Users, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { useView } from '../lib/access';
import { financialSectionVisible, programRef } from '../lib/access/types';
import { POTENTIAL_ISSUES_COPY, WAIVERS_TAILORING_COPY } from '../pages/Delivery/_shared/constants/sectionCopy';
import { useActivationGate } from '../pages/Delivery/Programs/activationGateHooks';
import { useProgram } from '../pages/Delivery/Programs/hooks';
const iconClass = 'w-4 h-4 flex-shrink-0';
// Delivery-focused taxonomy (SAD-1026). Home and Chat sit in the top
// ungrouped section; everything else lives in a labelled collapsible group.
export const PRIMARY_DEFS = [
    {
        id: 'home',
        label: 'Home',
        path: '',
        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
            className: iconClass
        }),
        testId: Testids.programNav.home,
        pinnable: false
    },
    {
        id: 'workroom',
        label: 'Chat',
        path: 'workroom',
        icon: /*#__PURE__*/ _jsx(MessagesSquare, {
            className: iconClass
        }),
        testId: Testids.programNav.workroom,
        pinnable: false
    }
];
export const GROUP_DEFS = [
    {
        id: 'delivery-management',
        label: 'Delivery Management',
        defaultOpen: true,
        items: [
            {
                id: 'deliverables',
                label: 'Deliverables',
                path: 'deliverables',
                icon: /*#__PURE__*/ _jsx(ListTodo, {
                    className: iconClass
                }),
                testId: Testids.cmNav.deliverables
            },
            {
                id: 'weekly-recap',
                label: 'Weekly Recap',
                path: 'weekly-recap',
                icon: /*#__PURE__*/ _jsx(CalendarClock, {
                    className: iconClass
                }),
                testId: Testids.programNav.weeklyRecap
            },
            {
                id: 'workstreams',
                label: 'Teams',
                path: 'workstreams',
                icon: /*#__PURE__*/ _jsx(Layers, {
                    className: iconClass
                }),
                testId: Testids.programNav.workstreams
            },
            {
                id: 'beyond-scope',
                label: 'Beyond Scope',
                path: 'beyond-scope',
                icon: /*#__PURE__*/ _jsx(FilePlus2, {
                    className: iconClass
                }),
                testId: Testids.programNav.beyondScope
            },
            {
                id: 'deviations',
                label: WAIVERS_TAILORING_COPY.label,
                path: 'waivers-tailoring',
                icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                    className: iconClass
                }),
                testId: Testids.programNav.deviations
            }
        ]
    },
    {
        id: 'program-health',
        label: 'Program Health',
        defaultOpen: false,
        items: [
            {
                id: 'quality',
                label: 'Quality',
                path: 'quality',
                icon: /*#__PURE__*/ _jsx(BarChart3, {
                    className: iconClass
                }),
                testId: Testids.programNav.quality
            },
            {
                id: 'risk',
                label: POTENTIAL_ISSUES_COPY.label,
                path: 'potential-issues',
                icon: /*#__PURE__*/ _jsx(Shield, {
                    className: iconClass
                }),
                testId: Testids.programNav.risk
            },
            {
                id: 'financial',
                label: 'Financial',
                path: 'financial',
                icon: /*#__PURE__*/ _jsx(DollarSign, {
                    className: iconClass
                }),
                testId: Testids.programNav.financial
            },
            {
                id: 'audits',
                label: 'Audits',
                path: 'audits',
                icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                    className: iconClass
                }),
                testId: Testids.programNav.audits
            }
        ]
    },
    {
        id: 'contract-details',
        label: 'Contract Details',
        defaultOpen: false,
        items: [
            {
                id: 'clins',
                label: 'CLINs',
                path: 'clins',
                icon: /*#__PURE__*/ _jsx(FileSpreadsheet, {
                    className: iconClass
                }),
                testId: Testids.programNav.clins
            },
            {
                id: 'periods',
                label: 'Periods',
                path: 'periods',
                icon: /*#__PURE__*/ _jsx(Calendar, {
                    className: iconClass
                }),
                testId: Testids.programNav.periods
            },
            {
                id: 'optional-tasks',
                label: 'Optional Tasks',
                path: 'optional-tasks',
                icon: /*#__PURE__*/ _jsx(ListChecks, {
                    className: iconClass
                }),
                testId: Testids.programNav.optionalTasks
            },
            {
                id: 'capabilities',
                label: 'Capabilities',
                path: 'capabilities',
                icon: /*#__PURE__*/ _jsx(Zap, {
                    className: iconClass
                }),
                testId: Testids.programNav.capabilities
            },
            {
                id: 'places',
                label: 'Places',
                path: 'places',
                icon: /*#__PURE__*/ _jsx(MapPin, {
                    className: iconClass
                }),
                testId: Testids.programNav.places
            },
            {
                id: 'glossary',
                label: 'Glossary',
                path: 'glossary',
                icon: /*#__PURE__*/ _jsx(BookA, {
                    className: iconClass
                }),
                testId: Testids.programNav.glossary
            }
        ]
    },
    {
        id: 'people-relationships',
        label: 'People & Relationships',
        defaultOpen: false,
        items: [
            {
                id: 'roles',
                label: 'Roles',
                path: 'roles',
                icon: /*#__PURE__*/ _jsx(Users, {
                    className: iconClass
                }),
                testId: Testids.programNav.roles
            },
            {
                id: 'companies',
                label: 'Companies',
                path: 'companies',
                icon: /*#__PURE__*/ _jsx(Building2, {
                    className: iconClass
                }),
                testId: Testids.programNav.companies
            },
            {
                id: 'contacts',
                label: 'Contacts',
                path: 'contacts',
                icon: /*#__PURE__*/ _jsx(Contact, {
                    className: iconClass
                }),
                testId: Testids.programNav.contacts
            },
            {
                id: 'touchpoints',
                label: 'Touchpoints',
                path: 'touchpoints',
                icon: /*#__PURE__*/ _jsx(Handshake, {
                    className: iconClass
                }),
                testId: Testids.programNav.touchpoints
            }
        ]
    },
    {
        id: 'program-administration',
        label: 'Program Administration',
        defaultOpen: false,
        items: [
            {
                id: 'activation-gate',
                label: 'Activation Gate',
                path: 'activation-gate',
                icon: /*#__PURE__*/ _jsx(ShieldCheck, {
                    className: iconClass
                }),
                testId: Testids.programNav.activationGate
            },
            {
                id: 'settings',
                label: 'Settings',
                path: 'settings',
                icon: /*#__PURE__*/ _jsx(Settings2, {
                    className: iconClass
                }),
                testId: Testids.cmNav.settings
            },
            {
                id: 'ops-audit',
                label: 'History',
                path: 'ops-audit',
                icon: /*#__PURE__*/ _jsx(Scan, {
                    className: iconClass
                }),
                testId: Testids.programNav.opsAudit
            },
            {
                id: 'closeout',
                label: 'Closeout',
                path: 'closeout',
                icon: /*#__PURE__*/ _jsx(ListChecks, {
                    className: iconClass
                }),
                testId: Testids.programNav.closeout
            }
        ]
    },
    {
        id: 'documents-mods',
        label: 'Documents and Mods',
        defaultOpen: true,
        items: [
            {
                id: 'docs',
                label: 'Docs',
                path: 'docs',
                icon: /*#__PURE__*/ _jsx(FileText, {
                    className: iconClass
                }),
                testId: Testids.programNav.docs
            }
        ]
    }
];
/** Palette search terms per section id — the rail has no use for them. */ export const SECTION_KEYWORDS = {
    workroom: [
        'workroom',
        'assistant'
    ],
    'weekly-recap': [
        'recap',
        'status report'
    ],
    workstreams: [
        'workstreams'
    ],
    'beyond-scope': [
        'out of scope',
        'change request'
    ],
    deviations: [
        'deviations',
        'waivers',
        'tailoring'
    ],
    risk: [
        'risk',
        'risks',
        'issues'
    ],
    financial: [
        'money',
        'budget',
        'EAC',
        'burn'
    ],
    clins: [
        'line items',
        'CLIN'
    ],
    periods: [
        'option periods',
        'period of performance',
        'PoP'
    ],
    'optional-tasks': [
        'options'
    ],
    capabilities: [
        'skills'
    ],
    roles: [
        'staffing',
        'LCAT'
    ],
    contacts: [
        'people',
        'POC'
    ],
    touchpoints: [
        'meetings',
        'growth'
    ],
    'activation-gate': [
        'activation',
        'gate',
        'readiness'
    ],
    settings: [
        'preferences',
        'configuration'
    ],
    'ops-audit': [
        'ops audit',
        'activity',
        'history'
    ],
    closeout: [
        'close out',
        'wrap up'
    ],
    docs: [
        'documents',
        'files',
        'modifications'
    ]
};
/**
 * The program's sections as the rail shows them: the visible groups plus the
 * program identity. One source for ProgramSidebar and the ⌘K palette so the
 * two never disagree about what a viewer can reach. Pass '' outside a program
 * (every query is disabled for an empty id).
 */ export function useProgramSections(programId) {
    // Permission-aware nav: hide the Financial item for viewers who can see
    // neither figure the Financial Health tab exposes — same predicate
    // KpiSpine's Financial tile uses (financialSectionVisible), so the nav and
    // the tile never disagree.
    const programView = useView(programRef(programId));
    const showFinancial = financialSectionVisible(programView.fieldMasked);
    // Touchpoints and growth opportunities are Growth data: ryugu refuses their
    // reads without area:growth on the program, so the item shows only where
    // the program grant carries it (and not while it resolves).
    const showGrowth = programView.granted('area:growth');
    // The activation gate only exists on converted (CommitImport) programs —
    // presence of the gate IS the origin signal; hidden until resolved so the
    // item never flashes (query cached, shared with ActivationGatePage).
    const { data: activationGate } = useActivationGate(programId);
    const showActivationGate = activationGate != null;
    const { data: programData } = useProgram(programId);
    const programName = programData?.program?.programNameShort || programData?.program?.programNameFull || 'Program';
    const contractNumber = programData?.program?.contractNumber || undefined;
    const visibleGroups = useMemo(()=>{
        const isVisible = (def)=>{
            if (def.id === 'financial') return showFinancial;
            if (def.id === 'touchpoints') return showGrowth;
            if (def.id === 'activation-gate') return showActivationGate;
            return true;
        };
        return GROUP_DEFS.map((group)=>({
                ...group,
                items: group.items.filter(isVisible)
            })).filter((group)=>group.items.length > 0);
    }, [
        showActivationGate,
        showFinancial,
        showGrowth
    ]);
    return {
        primary: PRIMARY_DEFS,
        groups: visibleGroups,
        programName,
        contractNumber
    };
}
